import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Sparkles } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";
import { API } from "../config";
import { trackEvent } from "../lib/analytics";

const GREETING =
  "Hi! I can help you figure out whether CardConnect could lower your processing costs. About how much does your business process in credit cards each month?";

const CHIPS = ["Under $25K", "$25K–$50K", "$50K–$100K", "$100K–$250K", "$250K+", "I'm not sure"];

const TypingDots = () => (
  <div className="flex items-center gap-1.5 rounded-2xl rounded-tl-sm bg-elevated px-4 py-3" data-testid="ai-typing">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="h-1.5 w-1.5 rounded-full bg-mint"
        animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
      />
    ))}
  </div>
);

export const AIAssistant = ({ onBook }) => {
  const sessionId = useRef(`web-${crypto.randomUUID()}`);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const started = useRef(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || typing) return;
    if (!started.current) {
      started.current = true;
      trackEvent("ai_chat_started");
    }
    trackEvent("ai_chat_message_sent");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setTyping(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId.current,
        message: msg,
      });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "Hmm, I had a little trouble with that. Try again — or tap the button below and a CardConnect specialist will help you directly.",
        },
      ]);
    } finally {
      setTyping(false);
    }
  };

  const showChips = messages.length === 1 && !typing;
  const showBook = messages.length >= 4;

  return (
    <section id="assistant" data-testid="ai-assistant" className="mx-auto max-w-7xl scroll-mt-24 px-6 py-24 sm:py-32">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <SectionTag index="05" label="AI Assistant" />
          <Reveal className="mt-6">
            <h2 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
              Meet Your AI <span className="text-mint">Payment Savings Assistant</span>
            </h2>
            <p className="mt-5 max-w-md leading-relaxed text-ink/60 sm:text-lg">
              Want a more accurate estimate? I'll ask you a few questions about your business and
              help identify where you may be overpaying — in under a minute.
            </p>
            <div className="mt-8 space-y-3 font-mono text-xs uppercase tracking-[0.2em] text-ink/40">
              <p>→ Answers in seconds</p>
              <p>→ No statement needed to start</p>
              <p>→ Estimates, never obligations</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="overflow-hidden rounded-3xl border border-ink/10 bg-surface shadow-2xl shadow-ink/10">
            <div className="flex items-center gap-3 border-b border-ink/10 px-6 py-4">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-mint opacity-60" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-mint" />
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-ink/60">
                CardConnect AI · Online
              </span>
              <Sparkles size={16} className="ml-auto text-mint" />
            </div>

            <div ref={scrollRef} className="flex h-80 flex-col gap-3 overflow-y-auto px-5 py-5" data-testid="ai-chat-messages">
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto rounded-br-sm bg-mint text-cream"
                      : "rounded-tl-sm bg-elevated text-ink"
                  }`}
                >
                  {m.text}
                </motion.div>
              ))}
              {typing && <TypingDots />}
            </div>

            <AnimatePresence>
              {showChips && (
                <motion.div exit={{ opacity: 0, height: 0 }} className="flex flex-wrap gap-2 px-5 pb-3" data-testid="ai-chips">
                  {CHIPS.map((c) => (
                    <button
                      key={c}
                      data-testid={`ai-chip-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      onClick={() => send(c)}
                      className="rounded-full border border-mint/40 px-4 py-2 font-mono text-xs text-mint transition-colors duration-300 hover:bg-mint hover:text-cream"
                    >
                      {c}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-center gap-2 border-t border-ink/10 p-4">
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Type your answer…"
                className="flex-1 rounded-full border border-ink/10 bg-elevated px-5 py-3 text-sm outline-none transition-colors placeholder:text-ink/35 focus:border-mint"
              />
              <button
                data-testid="ai-chat-send"
                onClick={() => send()}
                aria-label="Send message"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-mint text-cream transition-transform duration-300 hover:scale-105"
              >
                <Send size={16} />
              </button>
            </div>

            <AnimatePresence>
              {showBook && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="border-t border-ink/10 p-4"
                >
                  <button
                    data-testid="ai-book-cta"
                    onClick={onBook}
                    className="w-full rounded-full bg-mint py-3.5 font-display text-sm font-bold text-cream transition-transform duration-300 hover:scale-[1.02] hover:brightness-110"
                  >
                    REQUEST MY FREE REVIEW →
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
